import { useEffect, useState, useCallback } from "react"
import { API_BASE } from "../../../config/appConfig"
import { apiFetch } from "../../../utils/apiFetch"

function suggestionKey({ clinicId, treatmentId, start, duration }) {
  return JSON.stringify({
    clinicId: clinicId || "",
    treatmentId: treatmentId || "",
    start: start || "",
    duration: Number(duration || 0)
  })
}

function isBetterSuggestion(nextSuggestion, currentSuggestion) {
  if (!nextSuggestion) return false
  if (!currentSuggestion) return true

  if (nextSuggestion.found && !currentSuggestion.found) return true
  if (!nextSuggestion.found && currentSuggestion.found) return false

  const nextScore = Number(nextSuggestion.score || 0)
  const currentScore = Number(currentSuggestion.score || 0)

  if (nextScore !== currentScore) {
    return nextScore > currentScore
  }

  if (nextSuggestion.source === "API" && currentSuggestion.source !== "API") {
    return true
  }

  return false
}

function buildEmptySuggestion(reason = "No se encontró propuesta") {
  return {
    found: false,
    doctor: null,
    reason,
    score: 0,
    source: "LOCAL",
    alternatives: []
  }
}

function normalizeAlternative(alt) {
  if (!alt) return null

  const startValue = alt.startTime || alt.start || null
  if (!startValue) return null

  const startDate = new Date(startValue)
  if (isNaN(startDate.getTime())) return null

  const endValue = alt.endTime || null
  const endDate =
    endValue && !isNaN(new Date(endValue).getTime())
      ? new Date(endValue)
      : null

  const doctorName =
    alt.doctorName ||
    alt.doctor?.name ||
    "Doctor disponible"

  const specialty =
    alt.specialty ||
    alt.doctor?.specialty ||
    ""

  return {
    label:
      alt.label ||
      startDate.toLocaleTimeString("es-MX", {
        hour: "2-digit",
        minute: "2-digit"
      }),
    start: startDate.toISOString(),
    startTime: startDate.toISOString(),
    endTime: endDate ? endDate.toISOString() : null,
    doctorId: alt.doctorId || alt.doctor?.id || "",
    doctorName,
    specialty,
    compatibilityReason:
      alt.compatibilityReason ||
      "Horario disponible"
  }
}

function normalizeDoctorSuggestion(input, fallbackSource = "API") {
  if (!input) {
    return buildEmptySuggestion()
  }

  const found = Boolean(input.found)

  const doctor =
    input.doctor
      ? {
          id: input.doctor.id || "",
          name: input.doctor.name || "",
          specialty: input.doctor.specialty || ""
        }
      : null

  const alternatives = Array.isArray(input.alternatives)
    ? input.alternatives.map(normalizeAlternative).filter(Boolean)
    : []

  return {
    found,
    doctor,
    reason: input.reason || (found ? "Propuesta disponible" : "No se encontró propuesta"),
    score: Number(input.score || 0),
    source: input.source || fallbackSource,
    alternatives
  }
}

export default function useNewAppointmentEngine({
  clinicId,
  newAppt,
  setNewAppt,
  selectedTreatment,
  doctors,
  events
}) {
  const [doctorSuggestion, setDoctorSuggestion] = useState(null)
  const [alternatives, setAlternatives] = useState([])
  const [loadingSuggestion, setLoadingSuggestion] = useState(false)
  const [suggestingDoctor, setSuggestingDoctor] = useState(false)
  const [loadingAlternatives, setLoadingAlternatives] = useState(false)
  const [refiningSuggestion, setRefiningSuggestion] = useState(false)
  const [lastSuggestionKey, setLastSuggestionKey] = useState("")

  useEffect(() => {
    if (!selectedTreatment) return

    const base = selectedTreatment.durationMinutes || 30
    let adjusted = base

    if (selectedTreatment.complexity === "HIGH") {
      adjusted += 15
    }

    if (selectedTreatment.requiresAssistant) {
      adjusted += 10
    }

    setNewAppt((prev) =>
      prev.duration === adjusted
        ? prev
        : { ...prev, duration: adjusted }
    )
  }, [selectedTreatment, setNewAppt])

  const suggestDoctorLocal = useCallback(() => {
    if (!newAppt.start || !selectedTreatment) return

    setLoadingSuggestion(true)

    const start = new Date(newAppt.start)
    const end = new Date(start.getTime() + Number(newAppt.duration || 30) * 60000)

    const busyDoctors = new Set()

    events.forEach((ev) => {
      const evStart = new Date(ev.start)
      const evEnd = new Date(ev.end)
      const overlap = start < evEnd && end > evStart

      if (overlap) {
        busyDoctors.add(ev.extendedProps?.doctorId)
      }
    })

    const freeDoctor = doctors.find((d) => !busyDoctors.has(d.id))

    if (freeDoctor) {
      setDoctorSuggestion(
        normalizeDoctorSuggestion(
          {
            found: true,
            doctor: freeDoctor,
            reason: "Disponible según agenda actual",
            score: 1,
            source: "LOCAL",
            alternatives: []
          },
          "LOCAL"
        )
      )

      setNewAppt((prev) => ({
        ...prev,
        doctorId: freeDoctor.id
      }))

      setAlternatives([])
    } else {
      const alt = []

      for (let i = 1; i <= 4; i++) {
        const altStart = new Date(start.getTime() + i * 30 * 60000)

        alt.push({
          label: altStart.toLocaleTimeString("es-MX", {
            hour: "2-digit",
            minute: "2-digit"
          }),
          start: altStart.toISOString(),
          startTime: altStart.toISOString(),
          endTime: new Date(
            altStart.getTime() + Number(newAppt.duration || 30) * 60000
          ).toISOString(),
          doctorId: "",
          doctorName: "Doctor disponible",
          specialty: "",
          compatibilityReason: "Espacio disponible en agenda"
        })
      }

      setAlternatives(alt)

      setDoctorSuggestion(
        normalizeDoctorSuggestion(
          {
            found: false,
            doctor: null,
            reason: "No se encontró propuesta",
            score: 0,
            source: "LOCAL",
            alternatives: alt
          },
          "LOCAL"
        )
      )
    }

    setLoadingSuggestion(false)
  }, [
    newAppt.start,
    newAppt.duration,
    selectedTreatment,
    doctors,
    events,
    setNewAppt
  ])

  useEffect(() => {
    suggestDoctorLocal()
  }, [suggestDoctorLocal])

  const refineSuggestionWithApi = useCallback(async () => {
    if (!clinicId || !newAppt.treatmentId || !newAppt.start || !newAppt.duration) return

    const currentKey = suggestionKey({
      clinicId,
      treatmentId: newAppt.treatmentId,
      start: newAppt.start,
      duration: newAppt.duration
    })

    if (lastSuggestionKey === currentKey) return

    try {
      setRefiningSuggestion(true)

      const start = new Date(newAppt.start)
      const end = new Date(start)
      end.setMinutes(end.getMinutes() + Number(newAppt.duration || 30))

      const res = await apiFetch(`${API_BASE}/appointments/suggest-doctor`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          clinicId,
          treatmentId: newAppt.treatmentId,
          startTime: start.toISOString(),
          endTime: end.toISOString()
        })
      })

      const data = await res.json().catch(() => null)
      if (!res.ok || !data) return

      const normalizedSuggestion = normalizeDoctorSuggestion(data, "API")

      setDoctorSuggestion((prev) => {
        if (isBetterSuggestion(normalizedSuggestion, prev)) {
          return normalizedSuggestion
        }
        return prev
      })

      if (normalizedSuggestion.found && normalizedSuggestion.doctor?.id) {
        setNewAppt((prev) => {
          if (prev.doctorId) return prev
          return {
            ...prev,
            doctorId: normalizedSuggestion.doctor.id
          }
        })
      }

      if (normalizedSuggestion.alternatives.length > 0) {
        setAlternatives((prev) =>
          prev.length ? prev : normalizedSuggestion.alternatives
        )
      }

      setLastSuggestionKey(currentKey)
    } catch {
      // si falla API, conservamos sugerencia local
    } finally {
      setRefiningSuggestion(false)
    }
  }, [
    clinicId,
    newAppt.treatmentId,
    newAppt.start,
    newAppt.duration,
    lastSuggestionKey,
    setNewAppt
  ])

  useEffect(() => {
    if (!newAppt.treatmentId || !newAppt.start || !newAppt.duration) return
    refineSuggestionWithApi()
  }, [
    newAppt.treatmentId,
    newAppt.start,
    newAppt.duration,
    refineSuggestionWithApi
  ])

  useEffect(() => {
    setLastSuggestionKey("")
  }, [newAppt.treatmentId, newAppt.start, newAppt.duration])

  const suggestDoctor = useCallback(async () => {
    if (!clinicId || !newAppt.treatmentId || !newAppt.start || !newAppt.duration) {
      alert("Completa tratamiento, hora y duración")
      return
    }

    try {
      setSuggestingDoctor(true)

      const start = new Date(newAppt.start)
      const end = new Date(start)
      end.setMinutes(end.getMinutes() + Number(newAppt.duration || 30))

      const res = await apiFetch(`${API_BASE}/appointments/suggest-doctor`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          clinicId,
          treatmentId: newAppt.treatmentId,
          startTime: start.toISOString(),
          endTime: end.toISOString()
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo sugerir doctor")
        return
      }

      const normalizedSuggestion = normalizeDoctorSuggestion(data, "API")

      setDoctorSuggestion(normalizedSuggestion)

      if (normalizedSuggestion.found && normalizedSuggestion.doctor?.id) {
        setNewAppt((prev) => ({
          ...prev,
          doctorId: normalizedSuggestion.doctor.id
        }))
      }

      if (normalizedSuggestion.alternatives.length > 0) {
        setAlternatives(normalizedSuggestion.alternatives)
      }
    } catch {
      alert("No se pudo sugerir doctor")
    } finally {
      setSuggestingDoctor(false)
    }
  }, [clinicId, newAppt, setNewAppt])

  const loadAlternatives = useCallback(async () => {
    if (!clinicId || !newAppt.treatmentId || !newAppt.start || !newAppt.duration) return

    try {
      setLoadingAlternatives(true)

      const res = await apiFetch(`${API_BASE}/appointments/suggest-alternatives`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          clinicId,
          treatmentId: newAppt.treatmentId,
          desiredStart: new Date(newAppt.start).toISOString(),
          durationMinutes: Number(newAppt.duration || 30)
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        setAlternatives([])
        return
      }

      const normalizedAlternatives = Array.isArray(data?.suggestions)
        ? data.suggestions.map(normalizeAlternative).filter(Boolean)
        : []

      setAlternatives(normalizedAlternatives)

      setDoctorSuggestion((prev) => {
        if (!prev) {
          return buildEmptySuggestion("No se encontró propuesta")
        }

        return {
          ...prev,
          alternatives: normalizedAlternatives
        }
      })
    } catch {
      setAlternatives([])

      setDoctorSuggestion((prev) => {
        if (!prev) {
          return buildEmptySuggestion("No se pudieron cargar alternativas")
        }

        return {
          ...prev,
          alternatives: []
        }
      })
    } finally {
      setLoadingAlternatives(false)
    }
  }, [clinicId, newAppt])

  return {
    doctorSuggestion,
    alternatives,
    loadingSuggestion,
    suggestingDoctor,
    loadingAlternatives,
    refiningSuggestion,
    setDoctorSuggestion,
    setAlternatives,
    suggestDoctor,
    loadAlternatives
  }
}